/**
 * The open record, DOCKED beside the table (owner, 2026-10-05: "when opening
 * up boards in the table I do not want some disgustingly long dropdown that
 * is super difficult to navigate"). It replaces the panel that used to unfold
 * inside the table under its row.
 *
 *   - A labelled region to the right of the table (about 44% of the board,
 *     capped), so the table stays visible and scrollable with both scroll
 *     bars, and the open row stays highlighted.
 *   - Its own scroll, with a sticky header: the record's identity, where it
 *     sits on this page, Previous / Next (also Alt+Up / Alt+Down), and Close.
 *     On the Kanban (2026-10-30) the record grows with its content and the
 *     page or the workspace frame scrolls it, the header held in view: one
 *     scroll context, not a panel scrolling inside a scrolling page.
 *   - The header (2026-10-30): the name, an optional `meta` line from the host
 *     (building, status chips, owner), a compact "1 of 6", and icon buttons
 *     named for a screen reader with a CSS tooltip each.
 *   - Long content comes as SECTIONS: a sticky list of anchors at the top
 *     and one collapsible block per section, remembered per board.
 *     A single section (the `renderDetail` boards) is drawn without chrome.
 *   - Below 900px it is a full-height sheet over the board with a Back
 *     button; the table behind it is inert until the sheet closes. A sheet
 *     is a modal, so it is exposed as one (role=dialog, aria-modal, named).
 *
 * Opening, closing, Escape, focus and the URL are BoardTable's; this file
 * only draws what it is given.
 */
import React, { useCallback, useEffect, useState } from 'react';
// The phone sheet keeps Tab inside itself (FocusScope only; Radix Dialog does not load under CRA's Jest 27).
import { FocusScope } from '@radix-ui/react-focus-scope';
import { ArrowLeft, ChevronDown, ChevronUp, X } from 'lucide-react';
import { booleanRecord, readMemory, writeMemory } from './memory';

export interface BoardDetailSection {
  /** Stable id, used for the anchor and the remembered collapsed state. */
  id: string;
  /** The section's visible name in the anchor list and its header. */
  label: string;
  render: () => React.ReactNode;
  /** Start collapsed until the viewer opens it (then remembered). */
  defaultCollapsed?: boolean;
}

/** True below `maxWidth` (inclusive). False where matchMedia does not exist. */
export function useNarrow(maxWidth = 899): boolean {
  const query = `(max-width: ${maxWidth}px)`;
  const read = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : false);
  const [narrow, setNarrow] = useState(read);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(query);
    const on = () => setNarrow(mq.matches);
    on();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', on);
      return () => mq.removeEventListener('change', on);
    }
    mq.addListener(on);
    return () => mq.removeListener(on);
  }, [query]);
  return narrow;
}

function prefersReducedMotion(): boolean {
  try { return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

export interface DetailDockProps {
  id: string;
  /** The region's accessible name ("NORTH RIDGE: operations and Monday reference"). */
  label: string;
  /** The record's identity as the row shows it. */
  title: React.ReactNode;
  /** Optional, under the title: what the host says about the record at a glance (building, status, owner). Phrasing content. */
  meta?: React.ReactNode;
  /** 1-based place among the openable rows of this page, and how many there are. */
  index: number;
  count: number;
  onPrev: (() => void) | null;
  onNext: (() => void) | null;
  onClose: () => void;
  sheet: boolean;
  sections: BoardDetailSection[];
  memoryKey: string;
  headingRef: React.RefObject<HTMLHeadingElement>;
  /** The open record's key. It keys the CONTENT (not the dock), so switching records remounts a record's panel state while Prev/Next keep focus. */
  recordKey?: string;
}

export default function DetailDock({
  id, label, title, meta = null, index, count, onPrev, onNext, onClose, sheet, sections, memoryKey, headingRef, recordKey = '',
}: DetailDockProps) {
  const store = `sections.${memoryKey}`;
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => readMemory<Record<string, boolean>>(store, {}, booleanRecord));
  const [active, setActive] = useState<string | null>(null);
  const isCollapsed = (s: BoardDetailSection) => (Object.prototype.hasOwnProperty.call(collapsed, s.id) ? collapsed[s.id] : Boolean(s.defaultCollapsed));
  const setOne = useCallback((sid: string, value: boolean) => {
    setCollapsed((prev) => {
      const next = { ...prev, [sid]: value };
      writeMemory(store, next);
      return next;
    });
  }, [store]);
  const sectionId = (sid: string) => `${id}-sec-${sid}`;
  const toggleId = (sid: string) => `${id}-sec-${sid}-toggle`;

  // A new record starts with no anchor marked.
  useEffect(() => { setActive(null); }, [id, label]);

  const jump = (e: React.MouseEvent, s: BoardDetailSection) => {
    e.preventDefault();
    if (isCollapsed(s)) setOne(s.id, false);
    setActive(s.id);
    const el = document.getElementById(sectionId(s.id));
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    const btn = document.getElementById(toggleId(s.id)) as HTMLButtonElement | null;
    if (btn) btn.focus({ preventScroll: true });
  };

  const multi = sections.length > 1;
  const dock = (
    <section
      id={id}
      className={`board-dock${sheet ? ' board-dock--sheet' : ''}`}
      aria-label={label}
      {...(sheet ? { role: 'dialog', 'aria-modal': true } : {})}
      data-mode={sheet ? 'sheet' : 'docked'}
    >
      {/*
        The header (2026-10-30, owner: "a bit of mess when opening a resident record"): who the record is, then what
        the host says about it (meta: building, status, owner), and on the right a compact place ("1 of 6") with
        Previous / Next / Close as icon buttons. Each icon button is named for a screen reader and shows its name and
        shortcut as a tooltip on hover and keyboard focus. The tooltip is CSS only (board.css, [data-tip]): a Radix
        tooltip takes the next Escape, and Escape here belongs to the record (it closes it).
      */}
      <header className="board-dock__head">
        {sheet && (
          <button type="button" className="board-dock__back" onClick={onClose}>
            <ArrowLeft aria-hidden="true" size={16} strokeWidth={2} /> Back to the board
          </button>
        )}
        <div className="board-dock__top">
          <div className="board-dock__id">
            <h2 className="board-dock__title" ref={headingRef} tabIndex={-1}>{title}</h2>
            {meta ? <div className="board-dock__meta">{meta}</div> : null}
          </div>
          <div className="board-dock__nav-btns">
            {/* The full sentence for a screen reader (announced on Previous / Next); the eye gets "1 of 6". */}
            <p className="board-dock__where board-sr" aria-live="polite">Open record &middot; {index} of {count} on this page</p>
            <span className="board-dock__count" aria-hidden="true">{index} of {count}</span>
            <button type="button" className="board-dock__btn board-dock__icon" onClick={onPrev || undefined} disabled={!onPrev}
              aria-label="Previous record" aria-keyshortcuts="Alt+ArrowUp" data-tip="Previous (Alt+↑)">
              <ChevronUp aria-hidden="true" size={18} strokeWidth={2} />
            </button>
            <button type="button" className="board-dock__btn board-dock__icon" onClick={onNext || undefined} disabled={!onNext}
              aria-label="Next record" aria-keyshortcuts="Alt+ArrowDown" data-tip="Next (Alt+↓)">
              <ChevronDown aria-hidden="true" size={18} strokeWidth={2} />
            </button>
            {!sheet && (
              <button type="button" className="board-dock__btn board-dock__icon board-dock__close" onClick={onClose}
                aria-label="Close the open record" aria-keyshortcuts="Escape" data-tip="Close (Esc)">
                <X aria-hidden="true" size={18} strokeWidth={2} />
              </button>
            )}
          </div>
        </div>
      </header>
      <div className="board-dock__body board-detail" data-sections={sections.length}>
        {multi && (
          <nav className="board-dock__anchors" aria-label="Sections of the open record">
            <ul>
              {sections.map((s) => (
                <li key={s.id}>
                  <a href={`#${sectionId(s.id)}`} onClick={(e) => jump(e, s)} aria-current={active === s.id ? 'true' : undefined}>
                    {s.label}
                    {isCollapsed(s) && <span className="board-sr"> (collapsed)</span>}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}
        {multi ? sections.map((s) => {
          const shut = isCollapsed(s);
          const bodyId = `${id}-sec-${s.id}-body`;
          return (
            <section key={s.id} id={sectionId(s.id)} className="board-dock__section" aria-labelledby={toggleId(s.id)} data-collapsed={shut ? 'true' : 'false'}>
              <h3 className="board-dock__shead">
                <button type="button" id={toggleId(s.id)} aria-expanded={!shut} aria-controls={bodyId} onClick={() => setOne(s.id, !shut)}>
                  <span className="board-caret" aria-hidden="true" />
                  {s.label}
                </button>
              </h3>
              {/* Collapsed content stays mounted (hidden), so a form or a fetch inside it is not thrown away. */}
              <div key={`${recordKey}:${s.id}`} id={bodyId} className="board-dock__sbody" hidden={shut}>{s.render()}</div>
            </section>
          );
        }) : sections.map((s) => <div key={`${recordKey}:${s.id}`} className="board-dock__single" data-section={s.id}>{s.render()}</div>)}
      </div>
    </section>
  );
  // Sheet mode (a modal, #407): Tab and Shift+Tab loop inside the record instead of leaving through the document's
  // edge (verification of #414, 2026-10-14). Focus placement stays the host's -- the heading on open, the row or card on
  // close -- so neither auto-focus runs. Docked beside the board, nothing is trapped. The scope is always there (only
  // `trapped` / `loop` follow the mode), so a viewport crossing 900px never remounts the record or drops its focus.
  return (
    <FocusScope asChild trapped={Boolean(sheet)} loop={Boolean(sheet)} onMountAutoFocus={(e) => e.preventDefault()} onUnmountAutoFocus={(e) => e.preventDefault()}>
      {dock}
    </FocusScope>
  );
}
